import { AuthService } from '../../../../core/services/auth.service';
import { Component, OnInit, inject, signal, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { selectBranchId, selectCompanyId } from '../../../../core/store/auth/auth.selectors';
import * as AuthActions from '../../../../core/store/auth/auth.actions';
import { SettingsService } from '../../../../core/services/settings.service';
import { UploadService } from '../../../../core/services/upload.service';
import { toast } from 'ngx-sonner';
import { take, filter, switchMap, of, forkJoin } from 'rxjs';
import { ProceduresService } from '../../../../core/services/procedures.service';
import { TAX_REGIONS, toTaxRegion } from '../../../../core/utils/tax-region.util';

import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { DatePickerModule } from 'primeng/datepicker';
import { PasswordModule } from 'primeng/password';
import { InputMaskModule } from 'primeng/inputmask';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { AppButton } from '../../../../shared/components/app-button/app-button';
import { BookingSettingsService } from '../../../../core/services/booking-settings.service';
import { HasPermissionDirective } from '../../../../shared/directives/has-permission.directive';
import { DioService } from '../../../../core/services/dio.service';
import { PermissionService, MODULE } from '../../../../core/services/permission.service';
import { getIanaTimezones } from '../../../../shared/utils/timezone.utils';

@Component({
  selector: 'app-clinic-tab',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    InputTextModule,
    SelectModule,
    IconFieldModule,
    InputIconModule,
    DatePickerModule,
    PasswordModule,
    InputMaskModule,
    RadioButtonModule,
    ToggleSwitchModule,
    AppButton,
    HasPermissionDirective,
  ],
  templateUrl: './clinic-tab.html',
})
export class ClinicTab implements OnInit {
  private fb = inject(FormBuilder);
  private store = inject(Store);
  private settingsService = inject(SettingsService);
  private proceduresService = inject(ProceduresService);
  private uploadService = inject(UploadService);
  private translate = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);
  // Force refresh to resolve compilation error
  public authService = inject(AuthService);
  private perm = inject(PermissionService);
  private bookingSettings = inject(BookingSettingsService);

  /**
   * Whether this branch holds a CRM subscription. Gates the CRM-link toggle:
   * offering a switch for a product the clinic does not have is just noise in
   * a form that is already long.
   */
  hasCrmAccess = signal(false);

  get canEdit(): boolean {
    return this.perm.canEdit(MODULE.CONFIGURACOES);
  }

  private dioService = inject(DioService);
  /** Reflects dio_clinic_uuid, not the key - the key is write-only client-side. */
  dioConfigured = false;
  dioKeySaving = false;
  /** Which Dio clinic this branch points at. Their uuid, not ours to invent. */
  dioClinicUuid: string | null = null;
  dioClinicName: string | null = null;
  /** The shown clinic was linked by an EARLIER token and the current one has
   *  not confirmed it. Read from the branch, not held in memory: a reload used
   *  to make the screen look healthy again while the token was still wrong. */
  dioClinicUnverified = false;
  /** Clinics the saved token can see. Only shown when there is a choice to make. */
  dioClinics: { uuid: string; name: string }[] = [];
  dioClinicChoice: string | null = null;
  dioLinking = false;

  clinicForm!: FormGroup;

  // Which identifier is shown to users across the app for this company's patients.
  patientIdentifierOptions = [
    { label: 'patient_identifier_internal', value: 'internal' },
    { label: 'patient_identifier_external', value: 'external' },
  ];

  // Which tooth numbering notation is shown across Consultations (display-only —
  // the DB always stores FDI internally regardless of this setting).
  toothNotationOptions = [
    { label: 'tooth_notation_fdi', value: 'fdi' },
    { label: 'tooth_notation_universal', value: 'universal' },
  ];

  /** Portugal / Açores / Madeira. No "all" option: unset (null) shows none selected. */
  readonly taxRegionOptions = TAX_REGIONS.map((r) => ({ label: r.label, value: r.code }));

  // Clinic timezone: 'auto' (null) means fall back to each viewer's local time.
  timezoneOptions: { label: string; value: string | null }[] = [
    { label: 'clinic_timezone_auto', value: null },
    ...getIanaTimezones().map((tz) => ({ label: tz, value: tz })),
  ];

  isLoading = signal(true);
  isSaving = signal(false);
  companyId: number | null = null;
  countryCode = signal('PT');
  logoPreview = signal<string | null>(null);
  registrationLink = signal<string | null>(null);
  quickRegistrationLink = signal<string | null>(null);
  qrCodeUrl = signal<string | null>(null);
  quickQrCodeUrl = signal<string | null>(null);

  hours = [
    '00:00',
    '00:15',
    '00:30',
    '00:45',
    '01:00',
    '01:15',
    '01:30',
    '01:45',
    '02:00',
    '02:15',
    '02:30',
    '02:45',
    '03:00',
    '03:15',
    '03:30',
    '03:45',
    '04:00',
    '04:15',
    '04:30',
    '04:45',
    '05:00',
    '05:15',
    '05:30',
    '05:45',
    '06:00',
    '06:15',
    '06:30',
    '06:45',
    '07:00',
    '07:15',
    '07:30',
    '07:45',
    '08:00',
    '08:15',
    '08:30',
    '08:45',
    '09:00',
    '09:15',
    '09:30',
    '09:45',
    '10:00',
    '10:15',
    '10:30',
    '10:45',
    '11:00',
    '11:15',
    '11:30',
    '11:45',
    '12:00',
    '12:15',
    '12:30',
    '12:45',
    '13:00',
    '13:15',
    '13:30',
    '13:45',
    '14:00',
    '14:15',
    '14:30',
    '14:45',
    '15:00',
    '15:15',
    '15:30',
    '15:45',
    '16:00',
    '16:15',
    '16:30',
    '16:45',
    '17:00',
    '17:15',
    '17:30',
    '17:45',
    '18:00',
    '18:15',
    '18:30',
    '18:45',
    '19:00',
    '19:15',
    '19:30',
    '19:45',
    '20:00',
    '20:15',
    '20:30',
    '20:45',
    '21:00',
    '21:15',
    '21:30',
    '21:45',
    '22:00',
    '22:15',
    '22:30',
    '22:45',
    '23:00',
    '23:15',
    '23:30',
    '23:45',
  ];

  ngOnInit() {
    this.initForm();
    this.store
      .select(selectCompanyId)
      .pipe(
        filter((id) => !!id),
        take(1)
      )
      .subscribe((id) => {
        this.companyId = id;
        this.loadCompanyData();
        this.loadDioState();
        this.generateRegistrationLink();
      });

    // Subscription is per BRANCH while this form is per COMPANY, so it is read
    // separately rather than off the company row. hasCrmAccess already fails
    // closed on error, which is the behaviour we want: no answer, no toggle.
    this.store
      .select(selectBranchId)
      .pipe(
        filter((branchId) => !!branchId),
        take(1),
        switchMap((branchId) => this.bookingSettings.hasCrmAccess(branchId!)),
      )
      .subscribe((hasAccess) => {
        this.hasCrmAccess.set(hasAccess);
        this.cdr.detectChanges();
      });
  }

  private generateRegistrationLink() {
    const branchId = this.authService.userProfile?.branches_id;
    if (branchId) {
      const baseUrl = window.location.origin;

      const link = `${baseUrl}/public/registo-paciente/${branchId}`;
      this.registrationLink.set(link);
      this.qrCodeUrl.set(
        `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(link)}`
      );

      const quickLink = `${baseUrl}/public/registo-rapido/${branchId}`;
      this.quickRegistrationLink.set(quickLink);
      this.quickQrCodeUrl.set(
        `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
          quickLink
        )}`
      );
    }
  }

  copyLink() {
    const link = this.registrationLink();
    if (link) {
      navigator.clipboard.writeText(link).then(() => {
        toast.success(this.translate.instant('link_copied'));
      });
    }
  }

  copyQuickLink() {
    const link = this.quickRegistrationLink();
    if (link) {
      navigator.clipboard.writeText(link).then(() => {
        toast.success(this.translate.instant('link_copied'));
      });
    }
  }

  private initForm() {
    this.clinicForm = this.fb.group({
      // Clinic Information
      corporate_reason: ['', Validators.required],
      address_branches: ['', Validators.required],
      city: ['', Validators.required],
      establishment_address: ['', Validators.required],
      country: ['PT', Validators.required],
      postal_code: ['', Validators.required],
      opening_time: ['08:00', Validators.required],
      closing_time: ['18:00', Validators.required],

      // Patient identifier preference (internal per-branch serial vs external_id)
      patient_identifier_type: ['internal'],

      // Tooth numbering notation shown across Consultations (FDI vs Universal/USA)
      tooth_notation: ['fdi'],

      // Clinic timezone (null = use each viewer's local time)
      timezone: [null],

      // Whether the sidebar shows the link across to the CRM platform. The
      // control name has to match the companies_tb column exactly — onSubmit
      // spreads this whole form value straight into update().
      show_crm_link: [true],

      // GESFaturacao
      serieFatura: [''],
      nif: [''],
      username: [''],
      password: [''],

      // GESFaturação tax region whose VAT rates the tax pickers offer. NOT a
      // companies_tb column: it is branches_tb.tax_region (per branch), so
      // onSubmit strips it from the company payload and saves it to the
      // branch. null = nothing chosen yet, every rate offered.
      tax_region: [null],

      // SIBS
      'X-IBM-Client-Id-SIBS': [''],
      'authkey-SIBS': [''],

      // Diagnocat
      diagnocat_user: [''],
      diagnocat_pass: [''],
    });

    this.clinicForm.get('country')?.valueChanges.subscribe((val) => {
      this.countryCode.set(val);
      this.cdr.detectChanges();
    });
  }

  private loadDioState(onLoaded?: () => void) {
    const branchId = this.authService.userProfile?.branches_id;
    if (!branchId) return;
    this.dioService.getBranchClinic(branchId).subscribe(({ uuid, name, verified }) => {
      this.dioClinicUuid = uuid;
      this.dioClinicName = name;
      this.dioConfigured = !!uuid && verified;
      this.dioClinicUnverified = !!uuid && !verified;
      this.cdr.detectChanges();
      onLoaded?.();
    });
  }

  /**
   * Asks Dio which clinics the saved token can see. One clinic links straight
   * away, since there is nothing to choose; several present a dropdown rather
   * than guessing which of a group's clinics this branch is.
   */
  discoverDioClinics() {
    const branchId = this.authService.userProfile?.branches_id;
    if (!branchId || !this.canEdit) return;
    this.dioLinking = true;
    this.dioService.listDioClinics(branchId).subscribe({
      next: (clinics) => {
        this.dioLinking = false;
        this.dioClinics = clinics;
        // Confirmed only if the token can actually see the clinic we point at.
        // Clearing it on any successful listing meant a token belonging to a
        // DIFFERENT clinic showed a green check next to the old one - the very
        // case the stored stamp exists to catch. The function applies the same
        // rule server-side; this keeps the screen honest without a reload.
        this.dioClinicUnverified =
          !!this.dioClinicUuid && !clinics.some((c) => c.uuid === this.dioClinicUuid);
        if (clinics.length === 1) {
          this.linkDioClinic(clinics[0].uuid);
        } else {
          this.dioClinicChoice = this.dioClinicUuid ?? null;
          this.cdr.detectChanges();
        }
      },
      error: (err) => {
        this.dioLinking = false;
        // A clinic already linked by a previous token is now unconfirmed: the
        // screen must not keep presenting it as settled.
        this.dioClinicUnverified = !!this.dioClinicUuid;
        console.error('Error listing Dio clinics:', err);
        toast.error(err?.message ?? this.translate.instant('clinic_error_save'));
        this.cdr.detectChanges();
      },
    });
  }

  linkDioClinic(clinicUuid: string | null) {
    const branchId = this.authService.userProfile?.branches_id;
    if (!branchId || !clinicUuid || !this.canEdit) return;
    this.dioLinking = true;
    this.dioService.linkDioClinic(branchId, clinicUuid).subscribe({
      next: (linked) => {
        this.dioLinking = false;
        this.dioClinicUuid = linked?.uuid ?? clinicUuid;
        this.dioClinicName = linked?.name ?? null;
        this.dioClinicUnverified = false;
        this.dioConfigured = true;
        this.dioClinics = [];
        toast.success(
          this.translate.instant('clinic_dio_clinic_linked', { name: linked?.name ?? '' })
        );
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.dioLinking = false;
        console.error('Error linking Dio clinic:', err);
        toast.error(err?.message ?? this.translate.instant('clinic_error_save'));
        this.cdr.detectChanges();
      },
    });
  }

  saveDioKey(rawKey: string) {
    if (!this.canEdit) return;
    const key = rawKey.trim();
    const branchId = this.authService.userProfile?.branches_id;
    if (!key || !branchId) return;
    this.dioKeySaving = true;
    this.dioService.saveBranchApiKey(branchId, key).subscribe({
      next: () => {
        this.dioKeySaving = false;
        toast.success(this.translate.instant('clinic_dio_key_saved'));
        // Sequenced, not fired together: both write the verification state, and
        // whichever answered last used to win - a link confirmed a moment
        // earlier could end up displayed as unconfirmed.
        this.loadDioState(() => {
          // The token is what grants access to a clinic, so this is the moment
          // to resolve which one - it replaces a manual database edit.
          this.discoverDioClinics();
        });
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.dioKeySaving = false;
        console.error('Error saving Dio key:', err);
        toast.error(this.translate.instant('clinic_error_save'));
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * branches_tb.tax_region for the signed-in branch. Read separately because
   * the rest of this form is the company row; null leaves every radio
   * unselected.
   */
  private loadBranchTaxRegion() {
    const branchId = this.authService.userProfile?.branches_id;
    if (!branchId) return;
    this.settingsService.getBranchData(branchId).subscribe({
      next: (data: any) => {
        this.clinicForm.patchValue({ tax_region: toTaxRegion(data?.tax_region) });
        this.cdr.detectChanges();
      },
      error: (err: any) => console.error('Error loading branch tax region:', err),
    });
  }

  private loadCompanyData() {
    if (!this.companyId) return;

    this.loadBranchTaxRegion();
    this.isLoading.set(true);
    this.settingsService.getCompanyData(this.companyId).subscribe({
      next: (data: any) => {
        if (data) {
          this.countryCode.set(data.country || 'PT');
          this.logoPreview.set(data.image_url || null);
          this.clinicForm.patchValue({
            corporate_reason: data.corporate_reason,
            address_branches: data.address_branches,
            city: data.city,
            establishment_address: data.establishment_address,
            country: data.country || 'PT',
            postal_code: data.postal_code,
            opening_time: this.formatTime(data.opening_time),
            closing_time: this.formatTime(data.closing_time),
            patient_identifier_type: data.patient_identifier_type || 'internal',
            tooth_notation: data.tooth_notation || 'fdi',
            timezone: data.timezone ?? null,
            // ?? not ||: the column defaults to true and false is a real,
            // intentional value that must not be coerced back on.
            show_crm_link: data.show_crm_link ?? true,
            serieFatura: data.serieFatura,
            nif: data.nif,
            username: data.username,
            password: data.password,
            'X-IBM-Client-Id-SIBS': data['X-IBM-Client-Id-SIBS'],
            'authkey-SIBS': data['authkey-SIBS'],
            diagnocat_user: data.diagnocat_user,
            diagnocat_pass: data.diagnocat_pass,
          });
        }
        this.isLoading.set(false);
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        console.error('Error loading company data:', err);
        this.isLoading.set(false);
        this.cdr.detectChanges();
      },
    });
  }

  private formatTime(timeStr: string): string {
    if (!timeStr) return '08:00';
    // Postgres time might be HH:mm:ss, but we only want HH:mm for the dropdown
    return timeStr.substring(0, 5);
  }

  handleLogoSelect(event: any) {
    if (!this.canEdit) return;
    const file = event.target.files[0];
    if (file) {
      // Validate type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        toast.error(this.translate.instant('clinic_logo_invalid_type'));
        return;
      }

      // Validate size (5MB = 5 * 1024 * 1024 bytes)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error(this.translate.instant('clinic_logo_too_large'));
        return;
      }

      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.logoPreview.set(e.target.result);
        this.cdr.detectChanges();
      };
      reader.readAsDataURL(file);
    }
  }

  removeLogo() {
    if (!this.canEdit) return;
    this.logoPreview.set(null);
    // Logic to clear it from form/service will come later
    this.cdr.detectChanges();
  }

  onSubmit() {
    if (!this.canEdit) return;
    if (this.clinicForm.invalid || !this.companyId) {
      this.clinicForm.markAllAsTouched();
      toast.error(this.translate.instant('common_fix_errors'));
      return;
    }

    this.isSaving.set(true);
    const currentLogo = this.logoPreview();
    const isNewUpload = currentLogo && currentLogo.startsWith('data:image');

    let uploadObs = of(currentLogo);

    if (isNewUpload && currentLogo) {
      // Upload new image to storage
      uploadObs = this.uploadService.uploadImage(
        currentLogo,
        'user',
        `clinic/company_${this.companyId}`
      );
    } else if (!currentLogo) {
      // Logo was removed
      uploadObs = of(null);
    }

    uploadObs
      .pipe(
        switchMap((imageUrl) => {
          // tax_region is a branches_tb column, not a companies_tb one (see
          // initForm): keep it out of the company update and save it to the
          // signed-in branch in the same Save. Only when the radio was actually
          // changed: it loads separately, and a Save pressed before it arrived
          // would otherwise write the unselected state over the branch's region.
          const { tax_region, ...companyValues } = this.clinicForm.value;
          const formData = {
            ...companyValues,
            image_url: imageUrl,
          };
          const branchId = this.authService.userProfile?.branches_id;
          const regionChanged = !!this.clinicForm.get('tax_region')?.dirty;
          return forkJoin([
            this.settingsService.updateCompanyData(this.companyId!, formData),
            branchId && regionChanged
              ? this.settingsService.updateBranchData(branchId, { tax_region: toTaxRegion(tax_region) })
              : of(null),
          ]);
        })
      )
      .subscribe({
        next: () => {
          this.isSaving.set(false);
          // The tax pickers cache their lists per branch for the session;
          // drop them so a changed tax region applies straight away.
          this.proceduresService.clearTaxesCache();
          this.clinicForm.get('tax_region')?.markAsPristine();
          toast.success(this.translate.instant('clinic_success_save'));

          // Propagate the identifier preference to app-wide state so the change
          // takes effect immediately, without requiring a re-login.
          const idType = this.clinicForm.value.patient_identifier_type || 'internal';
          const notation = this.clinicForm.value.tooth_notation || 'fdi';
          // ?? not ||, for the same reason as the patch above: this one is a
          // boolean and `false` is the setting actually being made. It is what
          // the sidebar reads to show or hide the CRM link.
          const showCrmLink = this.clinicForm.value.show_crm_link ?? true;
          this.store.dispatch(
            AuthActions.updateUserInfo({
              userInfo: {
                patient_identifier_type: idType,
                tooth_notation: notation,
                show_crm_link: showCrmLink,
              },
            })
          );
          this.authService.setUserProfile({
            ...(this.authService.userProfile || {}),
            patient_identifier_type: idType,
            tooth_notation: notation,
            show_crm_link: showCrmLink,
          });

          this.loadCompanyData(); // Refresh to get the latest data
          this.cdr.detectChanges();
        },
        error: (err: any) => {
          this.isSaving.set(false);
          console.error('Error updating company data:', err);
          toast.error(this.translate.instant('clinic_error_save'));
          this.cdr.detectChanges();
        },
      });
  }
}
